import { Box, Chip, Skeleton } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import { Button, Modal } from 'react-bootstrap'
import DataGridTable from '../CommonFile/DataGridTable'
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { loadPaymentHistory } from '../redux/paymentService/pmt_action';
import { Circles } from 'react-loader-spinner';
import { capitalizeFirstLetter } from '../CommonFile/Function_utils';

function HistoryModal(props) {
    const dispatch = useDispatch();

    const [isLoading, setIsLoading] = useState(true)
    const [loadMore, setLoadMore] = useState(false)

    const { payment_history, history_total, history_pages } = useSelector(state => state.paymentdata);

    const [pagePagams, setPageParams] = useState({
        page: 1,
        page_size: 10
    })

    useEffect(() => {
        getHistoryData(pagePagams);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const getHistoryData = (filter) => {
        const postparams = {
            page: filter.page,
            page_size: filter.page_size,
            user_id: props.user_id,
            from_date: props.filterValues.from_date ? moment(props.filterValues.from_date).format("YYYY-MM-DD") : null,
            to_date: props.filterValues.to_date ? moment(props.filterValues.to_date).format("YYYY-MM-DD") : null,
        }

        dispatch(loadPaymentHistory(postparams)).then(
            (resp) => {
                setIsLoading(false)
                setLoadMore(false)
            }
        )
            .catch((err) => {
                setIsLoading(false)
                setLoadMore(false)
            });
    }

    // region Define DataGrid columns
    const columns1 = useMemo(() => {
        const baseColumns = [
            {
                field: 'item_name', headerName: 'Item Name', minWidth: 250,
                renderCell: (params) => {
                    if (params.row.isSkeleton) {
                        return <Skeleton variant="text" width="80%" />
                    }

                    return (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',         // Creates a clean space between avatar and text
                            fontWeight: '600'
                        }}>
                            <span>
                                {params.value ?? ""}
                            </span>
                        </div>
                    );
                }
            },
            {
                field: "currency",
                headerName: "currency",
                minWidth: 120,
                renderCell: (params) =>
                    params.row.isSkeleton ? (
                        <Skeleton variant="text" width="60%" />
                    ) : (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',         // Creates a clean space between avatar and text
                            fontWeight: '600'
                        }}>
                            <span>
                                {params.value ?? ""}
                            </span>
                        </div>
                    ),
            },
            {
                field: "value",
                headerName: "Value",
                minWidth: 120,
                renderCell: (params) =>
                    params.row.isSkeleton ? (
                        <Skeleton variant="text" width="60%" />
                    ) : (
                        params.value || "-"
                    ),
            },
            {
                field: "quantity",
                headerName: "Quantity",
                minWidth: 120,
                renderCell: (params) =>
                    params.row.isSkeleton ? (
                        <Skeleton variant="text" width="80%" />
                    ) : (
                        params.value || "-"
                    ),
            },
            {
                field: "plan",
                headerName: "Plan",
                minWidth: 120,
                renderCell: (params) =>
                    params.row.isSkeleton ? (
                        <Skeleton variant="text" width="95%" />
                    ) : (
                        <b>{capitalizeFirstLetter(params.value)}</b>
                    ),
            },
            {
                field: "subscription_period",
                headerName: "Duration",
                minWidth: 120,
                renderCell: (params) =>
                    params.row.isSkeleton ? (
                        <Skeleton variant="text" width="60%" />
                    ) : (
                        <b>{params.value}</b>
                    ),
            },
            {
                field: "additional_credits",
                headerName: "Credits(Additional)",
                minWidth: 200,
                renderCell: (params) =>
                    params.row.isSkeleton ? (
                        <Skeleton variant="text" width="60%" />
                    ) : (
                        <span ><b>{params.value}</b></span>
                    ),
            },
            {
                field: "",
                headerName: "Total",
                minWidth: 200,
                renderCell: (params) =>
                    params.row.isSkeleton ? (
                        <Skeleton variant="text" width="60%" />
                    ) : (
                        <span className='text-success'><b>{parseInt(params.row.quantity) + parseInt(params.row.additional_credits)}</b></span>
                    ),
            },
            {
                field: "payment_received_date",
                headerName: "Date",
                minWidth: 200,
                renderCell: (params) =>
                    params.row.isSkeleton ? (
                        <Skeleton variant="text" width="80%" />
                    ) : (
                        <Chip
                            label={moment(params.value).format("DD MMM YYYY • hh:mm A")}
                            size="small"
                            sx={{
                                fontWeight: 600,
                                backgroundColor: "var(--primary-pale-bg)",
                                color: "var(--primary-color)",
                                border: "1px solid #BFDBFE",
                                borderRadius: "8px",
                            }}
                        />
                    ),
            }
        ];

        return baseColumns;
    }, []);

    // #region handle scroll
    useEffect(() => {
        const container = document.querySelector('.MuiDataGrid-virtualScroller');
        if (!container) return;

        let lastScrollTop = 0;

        const handleScroll = () => {
            if (!container || loadMore) return;

            const currentScrollTop = container.scrollTop;

            if (currentScrollTop <= lastScrollTop) return;

            lastScrollTop = currentScrollTop;

            const distanceFromBottom = container.scrollHeight - currentScrollTop - container.clientHeight;

            if (distanceFromBottom === 0 && payment_history.length < history_total && !loadMore) {
                setLoadMore(true);
                setPageParams((prev) => {
                    if (prev.page >= history_pages) return prev;

                    return {
                        ...prev,
                        page: prev.page + 1,
                    };
                });
                const filter = {
                    ...pagePagams,
                    page: pagePagams.page + 1
                }
                getHistoryData(filter);
            }
        };

        container.addEventListener('scroll', handleScroll);

        // Cleanup function
        return () => {
            container.removeEventListener('scroll', handleScroll);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadMore, payment_history, history_total, setPageParams]);
    // #endregion

    // #region close modal function
    const closeModal = () => {
        props.setHistoryModal(false)
    }
    // #endregion

    const dataRows = isLoading
        ? Array.from({ length: 8 }, (_, index) => ({
            id: `skeleton-${index}`, // unique key
            isSkeleton: true,
        }))
        : payment_history.map((row, index) => ({
            ...row,
            id: row.id ?? row.user_id ?? `row-${index}`, // unique key for real rows
        }));

    return (
        <Modal
            show={props.show}
            // onHide={onHide}
            centered
            size="lg"
            className="history-modal"
        >
            <Modal.Body>
                <div className="text-center  px-3">

                    <div className='history-page-heading'>
                        <h3 className="mt-1 fw-bold">
                            History
                        </h3>
                        <Button
                            variant="outline-secondary"
                            onClick={closeModal}
                            className="px-4"
                        >
                            Close
                        </Button>
                    </div>
                    <hr className='m-0 mb-2' />


                    <Box sx={{ width: '100%', mt: 2 }}>
                        <DataGridTable rowData={dataRows}
                            columns={columns1} height="585px !important"
                            page="activity"
                        />
                        {(loadMore) && <div className='loader-css'>
                            <div className="d-flex justify-content-center align-items-center py-4">
                                <Circles
                                    height="30"
                                    width="30"
                                    color="#007bff"
                                    ariaLabel="circles-loading"
                                    visible={true}
                                />
                            </div>
                        </div>}
                    </Box>
                </div>

            </Modal.Body>
        </Modal>
    )
}

export default HistoryModal
